/**
 * Single time-series line chart, rendered as inline SVG.
 *
 * Modeled on History.js → ActivityChart: pure SVG, theme-aware via CSS
 * classes (no inline colour values), no chart library dependency.
 *
 * Props:
 *   title       string
 *   unit        string (e.g. "msg/s", "°C")
 *   series      [{ name, samples: [{ts, v}, ...], variant?: 'primary'|'secondary'|'muted' }]
 *   yMin, yMax  optional fixed bounds (otherwise auto)
 *   markers     [{ ts, label? }] vertical rules — receiver restarts. Without these a
 *               restart reads as an unexplained cliff in every counter-derived series at
 *               once, and the counters genuinely do reset there, so a reader cannot tell a
 *               restart from a real dropout.
 *   wide        render full-width and taller (one chart per row)
 */
function PerfChart({ title, unit, series, yMin, yMax, markers, wide }) {
  const all = (series || []).flatMap(s => s.samples || []).filter(p => p && p.v != null);
  if (all.length === 0) {
    return (
      <div className={`perf-chart${wide ? ' perf-chart-wide' : ''}`}>
        <div className="perf-chart-title">{title}</div>
        <div className="perf-chart-empty">No data</div>
      </div>
    );
  }

  const tsMin = Math.min(...all.map(p => p.ts));
  const tsMax = Math.max(...all.map(p => p.ts));
  const valMin = yMin !== undefined ? yMin : Math.min(...all.map(p => p.v));
  let valMax = yMax !== undefined ? yMax : Math.max(...all.map(p => p.v));
  if (valMax === valMin) valMax = valMin + 1;

  // Wide charts get a longer viewBox rather than just a wider CSS box: the SVG scales to fit,
  // so stretching a 400-unit viewBox would scale the text up with it and the axis labels would
  // end up larger than the title. More viewBox units = same text size, more room for detail —
  // which is the point of a 1-minute step.
  const viewW = wide ? 1000 : 400;
  const viewH = wide ? 190 : 130;
  const pad = { top: 10, right: 10, bottom: 22, left: 38 };
  const plotW = viewW - pad.left - pad.right;
  const plotH = viewH - pad.top - pad.bottom;

  const xOf = ts => pad.left + ((ts - tsMin) / Math.max(1, tsMax - tsMin)) * plotW;
  const yOf = v => pad.top + plotH - ((v - valMin) / (valMax - valMin)) * plotH;

  const gridY = 4;
  const gridLines = Array.from({ length: gridY + 1 }, (_, i) => valMin + (valMax - valMin) * i / gridY);

  return (
    <div className={`perf-chart${wide ? ' perf-chart-wide' : ''}`}>
      <div className="perf-chart-header">
        <span className="perf-chart-title">{title}</span>
        {unit && <span className="perf-chart-unit">{unit}</span>}
      </div>
      <svg
        className="perf-chart-svg"
        viewBox={`0 0 ${viewW} ${viewH}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {gridLines.map((val, i) => {
          const y = yOf(val);
          return (
            <g key={`g${i}`}>
              <line
                x1={pad.left} y1={y}
                x2={viewW - pad.right} y2={y}
                className="chart-gridline"
              />
              <text
                x={pad.left - 4} y={y + 3}
                className="chart-axis-label" textAnchor="end"
              >{formatVal(val)}</text>
            </g>
          );
        })}

        {/* Restart rules sit UNDER the data so a marker never hides the cliff it explains. */}
        {(markers || [])
          .filter(m => m && m.ts >= tsMin && m.ts <= tsMax)
          .map((m, i) => {
            const x = xOf(m.ts);
            return (
              <g key={`m${i}`} className="perf-chart-marker">
                <line x1={x} y1={pad.top} x2={x} y2={pad.top + plotH}
                      className="perf-chart-marker-line" />
                <text x={x + 3} y={pad.top + 8}
                      className="perf-chart-marker-label" textAnchor="start">
                  {m.label || 'restart'}
                </text>
              </g>
            );
          })}

        {(series || []).map((s, sIdx) => {
          const samples = (s.samples || []).filter(p => p && p.v != null);
          if (samples.length === 0) return null;
          const points = samples.map(p => `${xOf(p.ts).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(' ');
          const cls = `perf-chart-line perf-chart-line-${s.variant || 'primary'}`;
          return (
            <g key={`s${sIdx}`}>
              <polyline points={points} className={cls} fill="none" />
            </g>
          );
        })}

        <text
          x={pad.left} y={viewH - 4}
          className="chart-axis-label" textAnchor="start"
        >{formatTs(tsMin)}</text>
        <text
          x={viewW - pad.right} y={viewH - 4}
          className="chart-axis-label" textAnchor="end"
        >{formatTs(tsMax)}</text>
      </svg>

      {(series || []).length > 1 && (
        <div className="perf-chart-legend">
          {series.map((s, i) => (
            <span key={i} className={`perf-chart-legend-item perf-chart-legend-${s.variant || 'primary'}`}>
              {s.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function formatVal(v) {
  if (v == null) return '';
  const a = Math.abs(v);
  if (a >= 1000) return Math.round(v).toString();
  if (a >= 100) return v.toFixed(0);
  if (a >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

function formatTs(ms) {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export default PerfChart;
